import { BetTypeEnum } from '@/ui-components/components/BetLotto/BetLotto.types'
import {
  TWO_NUMBER_LIST,
  THREE_NUMBER_LIST,
  FunctionMenuEnum,
} from '../constants'

/**
 * Checks if the selected set contains any three-digit bet types
 * @param selected - Set of selected bet types
 * @returns boolean indicating if any three-digit bet types are selected
 */
const hasThreeDigit = (selected: Set<string>): boolean => {
  return THREE_NUMBER_LIST.some((type) => selected.has(type))
}

/**
 * Checks if the selected set contains any two-digit bet types
 * @param selected - Set of selected bet types
 * @returns boolean indicating if any two-digit bet types are selected
 */
const hasTwoDigit = (selected: Set<string>): boolean => {
  return (
    selected.has(BetTypeEnum.TWO_TOP) ||
    selected.has(BetTypeEnum.TWO_UNDER) ||
    selected.has(FunctionMenuEnum.SWAP_TWO)
  )
}

/**
 * Checks if the selected set contains any one-digit bet types
 * @param selected - Set of selected bet types
 * @returns boolean indicating if any one-digit bet types are selected
 */
const hasOneDigit = (selected: Set<string>): boolean => {
  return (
    selected.has(BetTypeEnum.RUN_TOP) ||
    selected.has(BetTypeEnum.RUN_BOTTOM) ||
    selected.has(FunctionMenuEnum.START_NUMBER) ||
    selected.has(FunctionMenuEnum.END_NUMBER)
  )
}

/**
 * Checks if the selected set contains any special two-digit bet types
 * @param selected - Set of selected bet types
 * @returns boolean indicating if any special two-digit bet types are selected
 */
export const hasTwoDigitSpecial = (selected: Set<string>): boolean => {
  return TWO_NUMBER_LIST.some((type) => selected.has(type))
}

/**
 * Counts how many two-number bet types are selected
 * @param selected - Array of selected bet types
 * @returns number of selected two-number bet types
 */
export const countDoubleNumberSelected = (selected: string[]): number => {
  return selected.filter((v) =>
    TWO_NUMBER_LIST.includes(v as (typeof TWO_NUMBER_LIST)[number]),
  ).length
}

/**
 * Updates the digit set based on the current selections
 * @param selected - Set of selected bet types
 * @param currentDigit - Current digit array
 * @returns Updated array of digits
 */
export const calculateDigits = (
  selected: Set<string>,
  currentDigit: number[],
): number[] => {
  const digitSet = new Set<number>(currentDigit)

  // Add or remove digits based on selection
  if (hasThreeDigit(selected)) {
    digitSet.add(3)
  } else {
    digitSet.delete(3)
  }

  if (hasTwoDigit(selected)) {
    digitSet.add(2)
  } else {
    digitSet.delete(2)
  }

  if (hasOneDigit(selected)) {
    digitSet.add(1)
  } else {
    digitSet.delete(1)
  }

  // Special case: if both two-digit special and regular two-digit are selected
  if (hasTwoDigitSpecial(selected) && hasTwoDigit(selected)) {
    digitSet.clear()
    digitSet.add(1)
  }

  // Default to 3 digits if nothing is selected
  if (digitSet.size === 0) {
    digitSet.add(3)
  }

  return Array.from(digitSet)
}
